<script setup lang="ts">
	import { Float } from '@headlessui-float/vue'
	import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
	import { EllipsisVerticalIcon } from '@heroicons/vue/24/solid'
	import moment from 'moment'

	import { HotelRate } from '@/interfaces/hotel'

	interface Props {
		rate: HotelRate
	}

	interface Emits {
		(e: 'actionModal', rate: HotelRate): void
		(e: 'selectRate', rate: HotelRate): void
		(e: 'toggleActiveModal', rate: HotelRate): void
		(e: 'actionModalDelete'): void
	}

	const props = defineProps<Props>()
	const emits = defineEmits<Emits>()

	const amountFormat = (value: string) => {
		return (
			new Intl.NumberFormat('es-US', {
				style: 'currency',
				currency: 'USD',
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
				currencyDisplay: 'symbol',
			}).format(Number(value)) + ` ${props.rate.currency}`
		)
	}

	const today = new Date()

	const checkIsExpired = (date: string) => {
		const expirationDate = new Date(date)
		return expirationDate < today
	}
</script>

<template>
	<div class="flex flex-col gap-4">
		<div
			:key="rate.id"
			:class="[
				'flex items-center justify-between rounded-lg border p-4 shadow-sm transition duration-200',
				rate.disabled ?
					'border-gray-300 bg-gray-100 text-gray-500 opacity-60 cursor-not-allowed'
				: rate.agreement ? 'border-blue-400 bg-blue-50'
				: 'border-gray-200 bg-white ',
			]"
		>
			<!-- Información de la tarifa -->
			<div class="flex flex-col">
				<p class="text-lg font-semibold">{{ rate.roomDetails }}</p>
				<p class="text-gray-600">{{ amountFormat(rate.amount) }}</p>

				<p class="text-sm text-gray-500">
					{{ moment(rate.expirationDate).fromNow() }}
					<span
						v-if="checkIsExpired(rate.expirationDate)"
						class="text-red-500"
					>
						(Expirada)
					</span>
				</p>
			</div>

			<!-- Indicador de acuerdo y logo -->
			<figure
				v-if="rate.agreement && !rate.disabled"
				class="flex items-center gap-2"
			>
				<div
					class="agreement fade-transition flex items-center rounded-md border-2 border-blue-100 bg-blue-50 p-1 text-blue-800 shadow-sm"
				>
					<p class="whitespace-nowrap text-sm font-semibold">
						Tarifa Enlaceforte
					</p>
				</div>

				<div class="rounded-full border-2 border-blue-500 bg-white p-1">
					<img
						src="@/assets/imgs/icono_enlace.png"
						class="w-6 transform transition duration-300 ease-in-out hover:scale-110"
						alt="Enlaceforte"
					/>
				</div>
			</figure>

			<Menu
				as="div"
				class="relative inline-block text-left menu-container"
			>
				<Float
					strategy="fixed"
					placement="bottom-end"
					:offset="4"
					:shift="true"
					:flip="true"
					:portal="true"
					:z-index="9999"
					enter="transition ease-out duration-100"
					enter-from="transform opacity-0 scale-95"
					enter-to="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leave-from="transform opacity-100 scale-100"
					leave-to="transform opacity-0 scale-95"
				>
					<MenuButton
						class="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-200 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
					>
						<EllipsisVerticalIcon
							class="h-5 w-5 text-gray-800"
							aria-hidden="true"
						/>
					</MenuButton>
					<MenuItems
						class="w-56 divide-y divide-gray-100 rounded-md shadow-lg ring-1 bg-white ring-black ring-opacity-5 focus:outline-none menu-items-container"
					>
						<div class="py-1">
							<MenuItem
								v-slot="{ active, close }"
								v-if="!rate.disabled && !checkIsExpired(rate.expirationDate)"
							>
								<button
									class="w-full"
									:class="[
										active ? 'bg-gray-100 text-gray-900' : 'text-gray-700 text',
										'group flex items-center px-4 py-2  text-sm gap-2',
									]"
									@click="
										() => {
											close()
											emits('actionModal', rate)
										}
									"
								>
									<small class="material-symbols-outlined text-gray-400"
										>edit
									</small>
									Editar
								</button>
							</MenuItem>
							<MenuItem
								v-slot="{ active }"
								v-if="!rate.disabled"
							>
								<button
									class="w-full"
									:class="[
										active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
										'group flex items-center px-4 py-2  text-sm gap-2',
									]"
									@click="
										() => {
											emits('actionModalDelete')
											emits('selectRate', rate)
										}
									"
								>
									<small class="material-symbols-outlined text-gray-400"
										>delete
									</small>
									Desactivar
								</button>
							</MenuItem>

							<MenuItem
								v-slot="{ active }"
								v-if="rate.disabled && !checkIsExpired(rate.expirationDate)"
							>
								<button
									class="w-full"
									:class="[
										active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
										'group flex items-center px-4 py-2  text-sm gap-2',
									]"
									@click="
										() => {
											emits('actionModalDelete')
											emits('selectRate', rate)
										}
									"
								>
									<small class="material-symbols-outlined text-gray-400"
										>check_circle
									</small>
									Habilitar
								</button>
							</MenuItem>

							<MenuItem v-slot="{ active }">
								<button
									class="w-full"
									:class="[
										active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
										'group flex items-center px-4 py-2  text-sm gap-2',
									]"
									@click="
										() => {
											emits('toggleActiveModal', rate)
										}
									"
								>
									<small class="material-symbols-outlined text-gray-400">
										comment
									</small>
									Ver comentarios/Cambios
								</button>
							</MenuItem>
						</div>
					</MenuItems>
				</Float>
			</Menu>
		</div>
	</div>
</template>

<style scoped>
	.fade-transition {
		transition: all 0.3s ease-in-out;
	}

	/* Estilos para asegurar que el menú siempre esté por encima */
	.menu-container {
		position: relative;
		z-index: 10;
	}

	/* Aplicar z-index alto a los elementos generados por Headless UI */
	:deep(.headlessui-float-content) {
		z-index: 9999 !important;
	}

	:deep(.menu-items-container) {
		z-index: 9999 !important;
		position: relative;
	}

	/* Asegurar que el portal se renderice por encima de todo */
	:deep([data-headlessui-portal]) {
		z-index: 9999 !important;
		position: relative;
	}
</style>
